import { Autocomplete, TextField } from '@mui/material';

import { useCustomInvestmentTags } from 'hooks/data/investment/useCustomInvestmentTags';
import {
  InvestmentCustomTagCategoryLabels,
  InvestorCustomTagCategoryLabels,
} from 'further-ui/labels';
import api from 'lib/trpcClient';

import { Schema as InvestorSchema } from 'further-types/investor';
import { CustomTag as InvestmentCustomTag } from 'further-types/investment';

type TagData = InvestmentCustomTag | InvestorSchema.InvestorCustomTag;

type Props = {
  onChange: (value: Array<TagData>) => void;
  value: Array<TagData>;
  mode: 'investor' | 'subscription';
  error?: boolean;
  helperText?: string;
  firmId?: string;
};

const MultiCustomTagSelector = ({
  onChange,
  value,
  error,
  helperText,
  mode,
  firmId,
}: Props) => {
  const mapCustomTags = (tag: TagData) => {
    const labelDictionary =
      mode === 'investor'
        ? InvestorCustomTagCategoryLabels
        : InvestmentCustomTagCategoryLabels;
    return {
      label: `(${labelDictionary[tag.category]}: ${tag.tag})`,
      value: tag,
    };
  };

  const { data: subscriptionCustomTags } = useCustomInvestmentTags({
    firmId,
    enabled: mode === 'subscription',
  });

  const subscriptionTagOptions = Object.entries(
    subscriptionCustomTags ?? {},
  ).flatMap(([category, tags]) => {
    return tags.map((tag) =>
      mapCustomTags({
        category: category as InvestmentCustomTag['category'],
        tag,
      }),
    );
  });

  const { data: investorTagData } = api.investor.listCustomTags.useQuery(
    {
      firmId: firmId ?? '',
    },
    {
      enabled: mode === 'investor',
      staleTime: 1,
    },
  );

  const investorTagOptions = (investorTagData ?? []).map((tag) =>
    mapCustomTags(tag),
  );

  return (
    <Autocomplete
      multiple
      size="small"
      options={
        mode === 'investor' ? investorTagOptions : subscriptionTagOptions
      }
      value={value.map(mapCustomTags) ?? []}
      onChange={(_, newValue) => {
        const uniqueValues = newValue.filter(
          (option, index, self) =>
            index ===
            self.findLastIndex(
              (o) =>
                o.value.category === option.value.category &&
                o.value.tag === option.value.tag,
            ),
        );

        onChange(uniqueValues.map((o) => o.value));
      }}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => String(option.value)}
      isOptionEqualToValue={(option, value) =>
        option.value.category === value.value.category &&
        option.value.tag === value.value.tag
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search tags"
          variant="outlined"
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};

export default MultiCustomTagSelector;
